import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Copy, Download, Loader2 } from "lucide-react";
import { API } from "@/lib/constants";
import { fetchPdfObjectUrl } from "@/lib/pdf";
import { useSeo } from "@/lib/seo";

export default function Success() {
  useSeo({
    title: "Order Confirmed — Flow Tweaks",
    description:
      "Your Flow Tweaks order is confirmed. Your order ID and next steps for unlocking your plan are below.",
    path: "/success",
    noindex: true,
  });

  const [status, setStatus] = useState("loading");
  const [orderId, setOrderId] = useState("");
  const [service, setService] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [copied, setCopied] = useState(false);
  const [guideAgreed, setGuideAgreed] = useState(false);
  const [guideUrl, setGuideUrl] = useState(null);
  const [guideBusy, setGuideBusy] = useState(false);
  const [guideError, setGuideError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) {
      setStatus("error");
      return;
    }
    setSessionId(sid);
    fetch(`/api/order-status?session_id=${sid}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.paid && data.orderId) {
          setOrderId(data.orderId);
          setService(data.service || "");
          setStatus("done");
          if (window.twq) {
            twq('event', 'tw-re25o-re25q', {});
          }
        } else {
          setStatus("error");
        }
      })
      .catch(() => setStatus("error"));
  }, []);

  // Console still has no app to unlock — pc/both are unlocked automatically in the app (a ticket
  // was created server-side by the Stripe webhook), nothing to claim manually.
  const isAppPlan = service === "pc" || service === "both";

  const copyId = () => {
    navigator.clipboard.writeText(orderId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getConsoleGuide = async () => {
    setGuideBusy(true);
    setGuideError(null);
    try {
      const url = await fetchPdfObjectUrl(`${API}/console-guide/from-session`, {
        session_id: sessionId,
        agree: true,
      });
      setGuideUrl(url);
    } catch (err) {
      setGuideError(err.message || "Couldn't fetch your guide — try again, or claim it via Discord below.");
    } finally {
      setGuideBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center px-4 text-center">
      {status === "loading" && (
        <p className="text-zinc-400 uppercase tracking-[0.25em] text-sm">Confirming payment...</p>
      )}

      {status === "error" && (
        <div className="max-w-md">
          <h1 className="text-3xl font-black uppercase mb-4" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            Something went wrong
          </h1>
          <p className="text-zinc-400 mb-6">
            We couldn't confirm your payment. If you were charged, join our Discord and open a ticket.
          </p>
          <Link to="/" className="text-orange-500 uppercase tracking-widest text-sm font-bold">
            Back home
          </Link>
        </div>
      )}

      {status === "done" && isAppPlan && (
        <div className="max-w-lg">
          <CheckCircle className="w-16 h-16 text-orange-500 mx-auto mb-6" />
          <h1 className="text-4xl font-black uppercase tracking-tighter mb-3" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            Payment Confirmed
          </h1>
          <p className="text-zinc-400 mb-8">
            Your tweaks are unlocked. Head back to the Flow Tweaks app — sign in with the same email
            you just paid with and you're in.
          </p>

          <p className="text-zinc-600 text-xs leading-relaxed">
            Don't have the app yet?{" "}
            <Link to="/#download" className="text-orange-500 underline">
              Download it here
            </Link>
            , then sign in from the Account tab — your unlock will already be there under Billing.
          </p>
        </div>
      )}

      {status === "done" && !isAppPlan && (
        <div className="max-w-lg">
          <CheckCircle className="w-16 h-16 text-orange-500 mx-auto mb-6" />
          <h1 className="text-4xl font-black uppercase tracking-tighter mb-3" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            Payment Confirmed
          </h1>
          <p className="text-zinc-400 mb-8">
            Console has no app to unlock — you'll get a PDF guide with the exact settings to
            change yourself, on your own console.
          </p>

          {!guideUrl && (
            <div className="border border-orange-500/40 bg-orange-500/5 p-6 mb-6 text-left">
              <label className="flex items-start gap-3 cursor-pointer mb-5">
                <input
                  type="checkbox"
                  checked={guideAgreed}
                  onChange={(e) => setGuideAgreed(e.target.checked)}
                  className="mt-0.5 accent-orange-500 w-4 h-4 shrink-0"
                />
                <span className="text-zinc-300 text-sm leading-relaxed">
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" className="text-orange-500 underline">
                    Terms &amp; Conditions
                  </Link>{" "}
                  and won't redistribute, resell, or publicly repost this guide.
                </span>
              </label>
              <button
                onClick={getConsoleGuide}
                disabled={!guideAgreed || guideBusy}
                className="w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-black font-black uppercase tracking-[0.2em] text-sm px-8 py-4 transition-all"
              >
                {guideBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                Agree &amp; Get My Guide
              </button>
              {guideError && <p className="text-red-400 text-xs mt-3">{guideError}</p>}
            </div>
          )}

          {guideUrl && (
            <a
              href={guideUrl}
              download="flow-tweaks-console-guide.pdf"
              className="inline-flex items-center gap-3 bg-orange-500 hover:bg-orange-400 text-black font-black uppercase tracking-[0.2em] text-sm px-8 py-4 transition-all mb-8"
            >
              <Download className="w-4 h-4" /> Download Your Guide
            </a>
          )}

          <div className="border border-white/10 bg-[#0a0a0a] p-6 mb-3">
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-2">Your Order ID</p>
            <div className="flex items-center justify-center gap-3">
              <span className="text-2xl font-black tabular-nums tracking-wider" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                {orderId}
              </span>
              <button onClick={copyId} className="text-zinc-400 hover:text-orange-500 transition-colors">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {copied && <p className="text-orange-500 text-xs mt-2 uppercase tracking-widest">Copied!</p>}
          </div>

          <p className="text-zinc-600 text-xs leading-relaxed">
            Save your order ID either way. If the guide above doesn't work, or you'd rather have
            it walked through with you,{" "}
            <button
              onClick={() => window.open("https://discord.gg/R5hEsssQgd", "_blank")}
              className="text-orange-500 underline"
            >
              open a ticket in Discord
            </button>{" "}
            and paste your order ID.
          </p>
        </div>
      )}
    </div>
  );
}
